import { createFileRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { startBusinessTypeSchema } from 'router/params/startBusinessType';
import { signupSearchParams } from 'router/search/signup';
import { headSignupSubRoute } from 'router/utils';

import { getCouponEligibilityQueryOptions } from 'services/partnersSelfSignup';

/**
 * Business-type template page — the entry point of the revamped welcome flow,
 * one route serving `/start/gym`, `/start/studio` and `/start/wellness_center`.
 *
 * A top-level route rather than a `/_signup` child: that layout requires an
 * existing lead (`beforeLoadSignupSubRoute`), and this page runs *before* any
 * lead exists, exactly like `/`. It therefore brings its own
 * `LeadProvider`/`StepsProvider` (see the lazy half of this route).
 */
export const Route = createFileRoute('/start/$businessType')({
  validateSearch: zodValidator(signupSearchParams),
  head: headSignupSubRoute,
  beforeLoad: ({ context, params }) => {
    const businessType = startBusinessTypeSchema.safeParse(params.businessType);

    if (!businessType.success) {
      throw redirect({ to: '/', search: true });
    }

    const isWellnessCenterOn = context.growthbook.isOn(
      'partners.acquisition.wellness-center',
    );

    if (businessType.data === 'wellness_center' && !isWellnessCenterOn) {
      throw redirect({ to: '/', search: true });
    }

    // Returned into the route context so consumers get the narrowed
    // `StartBusinessType` instead of the raw `string` path param, with the
    // validation happening exactly once, here.
    return { businessType: businessType.data };
  },
  loaderDeps: ({ search }) => ({ couponCode: search.ssu_coupon }),
  loader: async ({ context, deps, preload }) => {
    const isCouponCodeOn = context.growthbook.isOn(
      'partners.acquisition.coupon-code',
    );

    if (!isCouponCodeOn) return;

    const couponEligibilityResponse = await context.queryClient.ensureQueryData(
      getCouponEligibilityQueryOptions(deps.couponCode),
    );

    if (!preload && couponEligibilityResponse?.status === 'not_found') {
      throw redirect({
        to: '.',
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          ssu_coupon: undefined,
        }),
        replace: true,
      });
    }
  },
});
